/**
 * theme-custom.js
 * -----------------------------------------------------------------------------
 * Consolidated, deferred theme scripts that contain NO Liquid.
 * Loaded with `defer` from layout/theme.liquid, so it runs after the document
 * is parsed but before DOMContentLoaded. jQuery is loaded synchronously earlier
 * in the head, so `$` is guaranteed to be available where used.
 *
 * Liquid-dependent code (analytics params, customer data, translations, the
 * gtag/GTM bootstrap and the GPC consent gate) intentionally remains inline in
 * theme.liquid — Shopify does not render Liquid inside /assets files, and the
 * consent gate must run as early as possible.
 *
 * Simple jQuery snippets have been rewritten in vanilla JS; complex
 * accessibility DOM-manipulation kept its jQuery implementation to avoid
 * behavioural regressions.
 * -----------------------------------------------------------------------------
 */
(function () {
    'use strict';

    const $ = window.jQuery;
    const bodyClass = document.body.className;
    const ready = (fn) =>
        document.readyState === 'loading'
            ? document.addEventListener('DOMContentLoaded', fn, {once: true})
            : fn();

    // True when a MutationObserver batch touched anything matching `selector`:
    // a changed node inside one, or an added subtree holding one. The
    // page-wide observers below use it to skip their document scan for the
    // hundreds of unrelated app mutations during load (~1.2s of
    // querySelectorAll on a throttled phone on /collections/inks, profiled
    // 10/23/2026).
    const touches = (records, selector) => records.some((r) => {
        const t = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (t && t.closest(selector)) return true;
        return Array.prototype.some.call(r.addedNodes, (n) =>
            n.nodeType === 1 && (n.matches(selector) || n.querySelector(selector) !== null));
    });

    /* =========================================================================
     * Accessibility — product template (jQuery, complex DOM work)
     * ========================================================================= */
    if (bodyClass.includes('template-product')) {
        $(function () {
            setTimeout(function () {
                $('.jdgm-form__fieldset').each(function () {
                    const elID = $(this).children('label').attr('for') + '-label';
                    $(this).children('label').attr('id', elID);
                    $(this).children('input').attr('aria-labelledby', elID);
                });

                $('.jdgm-form .jdgm-question-form input').removeAttr('aria-labelledby');
                $('#jm-widget-main .jdgm-form__fieldset label').remove();

                $('.jdgm-form .jdgm-form__fieldset label').replaceWith(function () {
                    return '<div>' + $(this).html() + '</div>';
                });
            }, 3500);

            $('.pswp__img').attr('alt', 'Altenew');
            $('#jdgm_question_reviewer_name').removeAttr('aria-labelledby');
        });
    }

    /* =========================================================================
     * Accessibility — page template (jQuery, complex DOM work)
     * ========================================================================= */
    if (bodyClass.includes('template-page')) {
        $(function () {
            setTimeout(function () {
                $('.calendar-date').each(function () {
                    $(this)
                        .find('.calendar-events .tsc-tooltip-dropdown .tooltip-event img')
                        .attr('alt', 'Altenew');
                });

                $('.time.tsc--eventModalLink, .title.tsc--eventModalLink').each(function () {
                    const title = $(this).data('event-title');
                    $(this).prepend("<span class='visually-hidden'>" + title + '</span>');
                });

                $('.template-page-pf-1a9f6f98 img').each(function () {
                    const imgAlt = $(this).attr('alt');
                    if (imgAlt && imgAlt.length <= 0) $(this).attr('alt', 'Altenew');
                });

                $('img:not([alt])').attr('alt', 'Altenew');
                $('.df-share-url, .df-share-button.df-share-mail.ti-email').remove();

                $('.pf-c').each(function () {
                    const imgAlt = $(this).find('a img').attr('alt');
                    const label = imgAlt !== undefined ? imgAlt : 'Altenew';
                    $(this)
                        .find('a')
                        .prepend("<span class='visually-hidden hidden-span'>" + label + '</span>');
                });
            }, 3500);

            $('#layeringGuidePage a[title]').each(function () {
                const $link = $(this);
                const text = $.trim($link.text());
                const title = $.trim($link.attr('title') || '');
                if (text === title) $link.removeAttr('title');
            });
        });
    }

    /* =========================================================================
     * Accessibility — global image alt / aria fixes (jQuery, runs everywhere)
     * Deferred to idle time so it does not compete with first paint / LCP.
     * ========================================================================= */
    function runGlobalA11yFixes() {
        var jq = window.jQuery;
        if (!jq) return;
        jq(function () {
            jq('img').each(function () {
                if (jq(this).attr('alt') === 'Image') {
                    jq(this).attr('alt', 'Altenew: Craft your life');
                }
                jq(this).removeAttr('title');
            });
            jq('img:not([alt])').attr('alt', 'Altenew');

            setTimeout(function () {
                jq('.gpsr-container').each(function () {
                    jq(this).children('h5').attr('aria-level', '3');
                });

                jq('.jdgm-carousel-item__product-image').attr('role', 'button').attr('tabindex', '0');
                jq('.pdf-embed div.df-ui-btn').removeAttr('title');

                jq("<label for='tagsFilter' class='visually-hidden'><span>Instructor</span></label>").insertBefore('#tagsFilter');
                jq("<label for='customTechFilter' class='visually-hidden'><span>Technique</span></label>").insertBefore('#customTechFilter');

                jq('#pc--browser-opt-out-checkbox').wrap("<label><span class='visually-hidden'>Opt out</span></label>");
                jq("textarea[name='g-recaptcha-response']").wrap("<label><span class='visually-hidden'>Recaptcha</span></label>");
                jq("textarea[name='h-captcha-response']").wrap("<label><span class='visually-hidden'>Captcha</span></label>");
            }, 3500);

            jq('.coursesSearchElement').attr('aria-label', 'Search');
        });
    }

    if (window.requestIdleCallback) {
        window.requestIdleCallback(runGlobalA11yFixes, { timeout: 6000 });
    } else {
        window.addEventListener('load', function () { setTimeout(runGlobalA11yFixes, 2000); }, { once: true });
    }

    /* =========================================================================
     * Subscription banner images, Loop drawer redirect, mobile review badges
     * ========================================================================= */
    $(function () {
        setTimeout(function () {
            const $bannerLinks = $('#subscriptionBanner a');
            const allMatch =
                $bannerLinks.length > 0 &&
                $bannerLinks.toArray().every((el) => $(el).text().trim().startsWith('LinkImage:'));

            if (allMatch) {
                $bannerLinks.each(function () {
                    const $link = $(this);
                    const imageSrc = $link.text().replace('LinkImage: ', '').trim();
                    $link.html($('<img>', {src: imageSrc, alt: 'Subscription Icon'}));
                });
            }

            // Loop product drawer: make titles navigate to the product
            document.addEventListener(
                'click',
                function (event) {
                    const target = event.target.closest('.loop-product-drawer-title');
                    if (!target) return;
                    event.stopPropagation();

                    let productUrl =
                        target.getAttribute('href') ||
                        target.closest('a')?.getAttribute('href') ||
                        target.closest('[data-product-url]')?.dataset.productUrl ||
                        target.closest('[data-url]')?.dataset.url;

                    if (!productUrl) {
                        const handle =
                            target.closest('[data-handle]')?.dataset.handle ||
                            target.closest('[data-product-handle]')?.dataset.productHandle;
                        if (handle) productUrl = `/products/${handle}`;
                    }

                    if (productUrl) window.location.href = productUrl;
                },
                true
            );
        }, 2000);

        /*
         * Mobile (<768px): show only the review count on the Judge.me badge.
         *
         * This used to be a 500ms interval that gave up after 30 seconds, so
         * whether a PDP ended up reading "12 reviews" or just "12" depended on
         * when Judge.me happened to (re)render its widget — which is why the
         * label was inconsistent from one product page to the next. Watch for
         * re-renders instead, and keep the full label around so widening the
         * window puts the word back.
         *
         * A retired product's own badge keeps the word at every width. Its buy
         * column is centred on a phone with nothing beside the stars, so a bare
         * "9" read as a stray number (86d47q9na, 09/10). Product cards further
         * down the same page still shorten.
         */
        const badgeMedia = window.matchMedia('(max-width: 767px)');
        const isRetiredPage = document.body.classList.contains('is-retired-product');

        function syncReviewBadges() {
            document.querySelectorAll('.jdgm-prev-badge__text').forEach((el) => {
                const text = el.textContent.trim();
                // Only Judge.me's own output contains the word, so this never
                // re-captures a label we already shortened.
                if (/review/i.test(text)) el.dataset.jdgmFullLabel = text;

                const full = el.dataset.jdgmFullLabel;
                if (!full) return;

                const keepWord = isRetiredPage && el.closest('.product-single__meta');
                const wanted = badgeMedia.matches && !keepWord ? full.replace(/reviews?/i, '').trim() : full;
                if (text !== wanted) el.textContent = wanted;
            });
        }

        syncReviewBadges();
        badgeMedia.addEventListener('change', syncReviewBadges);

        let badgeSyncQueued = false;
        new MutationObserver((records) => {
            if (badgeSyncQueued || !touches(records, '.jdgm-prev-badge')) return;
            badgeSyncQueued = true;
            requestAnimationFrame(() => {
                badgeSyncQueued = false;
                syncReviewBadges();
            });
        }).observe(document.body, {childList: true, subtree: true, characterData: true});
    });

    /* =========================================================================
     * Sticky add-to-cart bar: toggle visibility based on the main CTA
     * ========================================================================= */
    ready(function () {
        const mainCta = document.querySelector('#addToCartCTA');
        const stickyBar = document.querySelector('#sticky-bar-CTA');
        if (!mainCta || !stickyBar) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
                });
            },
            {threshold: 0}
        );
        observer.observe(mainCta);
    });

    /* =========================================================================
     * Judge.me review carousel: make product images clickable
     * ========================================================================= */
    function fixJdgmAccessibility() {
        document.querySelectorAll('.jdgm-carousel-item').forEach((item) => {
            const image = item.querySelector('.jdgm-carousel-item__product-image');
            const link = item.querySelector('.jdgm-carousel-item__product');
            const titleEl = item.querySelector('.jdgm-carousel-item__product-title');

            if (image) {
                if (!image.getAttribute('role')) image.setAttribute('role', 'button');
                if (!image.getAttribute('tabindex')) image.setAttribute('tabindex', '0');
                if (titleEl && !image.getAttribute('aria-label')) {
                    image.setAttribute('aria-label', 'View product ' + titleEl.textContent.trim());
                }
                
                // Add click listener if not already bound
                if (link && !image.dataset.clickBound) {
                    image.dataset.clickBound = 'true';
                    image.addEventListener('click', () => {
                        window.location.href = link.href + '#';
                    });
                }
            }
            if (link && titleEl) {
                if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
                    link.setAttribute('aria-label', 'View reviews for ' + titleEl.textContent.trim());
                }
            }
        });
    }

    // Run the fix when Judge.me widgets finish rendering
    document.addEventListener('jdgm.done', function () {
        fixJdgmAccessibility();
    });

    ready(function () {
        fixJdgmAccessibility();
        setTimeout(fixJdgmAccessibility, 3500);
    });

    /* =========================================================================
     * Blog: reload when the "show per page" selector changes (vanilla)
     * ========================================================================= */
    ready(function () {
        const select = document.querySelector('.show-per-page select.num');
        if (select) {
            select.addEventListener('change', function () {
                window.location.replace(this.value);
            });
        }
    });

    /* =========================================================================
     * Collection: change the grid "view" via the #sel1 selector (vanilla)
     * ========================================================================= */
    ready(function () {
        const sel1 = document.querySelector('#sel1');
        if (!sel1) return;
        sel1.addEventListener('change', function () {
            const base = window.location.href.split('?')[0];
            let sort = '';
            if (window.location.href.includes('sort_by')) {
                sort = window.location.href.split('sort_by=')[1];
            }
            let url = `${base}?view=${this.value}`;
            if (sort) url += `&sort_by=${sort}`;
            window.location.replace(url);
        });
    });

    /* =========================================================================
     * Mobile collection/listing: show only the review count on Judge.me badges
     * ========================================================================= */
    ready(function () {
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        const onProduct = window.location.href.includes('/products/');
        if (onProduct || !isMobile) return;

        setTimeout(function () {
            const badges = document.querySelectorAll('.jdgm-prev-badge');
            const texts = document.querySelectorAll('.jdgm-prev-badge__text');
            badges.forEach((badge, i) => {
                if (texts[i]) texts[i].innerText = badge.getAttribute('data-number-of-reviews');
            });
        }, 2500);
    });

    /* =========================================================================
     * Rename "Add to cart" → "Add to Bag" (inputs + button spans, observed)
     * ========================================================================= */
    ready(function () {
        document.querySelectorAll('.add-to-cart__button input[type="submit"]').forEach((input) => {
            if (input.value.toLowerCase() === 'add to cart') input.value = 'Add to Bag';
        });

        const forceAddToBag = () => {
            const selectors =
                'button[name="add"] span[data-add-to-cart-text], .product-sticky-header .add-to-cart span';
            document.querySelectorAll(selectors).forEach((span) => {
                if (span.textContent.trim().toLowerCase() === 'add to cart') {
                    span.textContent = 'Add to Bag';
                }
            });
        };

        forceAddToBag();

        // Still synchronous, so "Add to cart" never paints, but only for
        // batches that touch an add button.
        const observer = new MutationObserver((records) => {
            if (!touches(records, 'button[name="add"], .product-sticky-header')) return;
            observer.disconnect();
            forceAddToBag();
            observer.observe(document.body, {childList: true, subtree: true, characterData: true});
        });
        observer.observe(document.body, {childList: true, subtree: true, characterData: true});
    });

    /* =========================================================================
     * Wishlist: hide cart entries for restricted products after the app fetch.
     * Populate `restrictedProducts` per monthly release (currently none).
     * ========================================================================= */
    // Only installed while the list has products: with it empty the proxy
    // wrapped every fetch on the site for nothing. The rejection handler on
    // .then() matters: without it every failed beacon (a blocked GA collect,
    // say) surfaced as an uncaught "Failed to fetch" page error (10/23/2026).
    const restrictedProducts = [];
    if (restrictedProducts.length) {
        try {
            window.fetch = new Proxy(window.fetch, {
                apply(fetch, thisArg, args) {
                    const result = fetch.apply(thisArg, args);
                    result.then(() => {
                        if (args[0]?.search && args[0].search(/apps-wishlist/) >= 0) {
                            const interval = setInterval(function () {
                                if (document.querySelector('.wishlist-cart')) {
                                    restrictedProducts.forEach((id) => {
                                        document.querySelector(`.wishlist-cart[data-product_id="${id}"]`)?.remove();
                                    });
                                    clearInterval(interval);
                                }
                            }, 50);
                        }
                    }, () => {});
                    return result;
                }
            });
        } catch (err) {
            console.error('Wishlist cart fetch proxy failed:', err);
        }
    }

    /* =========================================================================
     * Product cards: the Judge.me stars sit outside the card's link, so a click
     * on "★★★★★ 139 reviews" did nothing (dead-click sweep on /collections/inks,
     * 10/23/2026). Send it to that product's reviews instead. Capture phase:
     * Judge.me's own badge handler stops the click, and on its own it opened
     * the product without the reviews on desktop and nothing on phones.
     * ========================================================================= */
    document.addEventListener('click', function (e) {
        const badge = e.target.closest && e.target.closest('.grid-product .jdgm-prev-badge');
        if (!badge || badge.closest('a')) return;
        const link = badge.closest('.grid-product')?.querySelector('a.grid-product__link[href]');
        if (!link) return;
        e.preventDefault();
        e.stopPropagation();
        window.location.href = link.href.split('#')[0] + '#judgeme_product_reviews';
    }, true);

    /* =========================================================================
     * Gorgias chat: hide "Powered by" once the widget initialises
     * ========================================================================= */
    const gorgiasReady = window.GorgiasChat
        ? window.GorgiasChat.init()
        : new Promise((resolve) =>
            window.addEventListener('gorgias-widget-loaded', resolve, {once: true})
        );
    gorgiasReady.then(() => window.GorgiasChat?.hidePoweredBy(true));

    /* =========================================================================
     * Gorgias chat: track button clicks in Microsoft Clarity
     * ========================================================================= */
    document.addEventListener('click', function (e) {
        const chatButton = e.target.closest('.custom-chat-icon, img[alt="Chat Icon"]');
        if (chatButton && typeof window.clarity === 'function') {
            window.clarity('event', 'gorgias_chat_click');
        }
    });

    /* =========================================================================
     * Microsoft Clarity (loads with this deferred bundle)
     * -------------------------------------------------------------------------
     * Clarity ignores Google Consent Mode, so injecting its tag unconditionally
     * fired session-recording beacons BEFORE the visitor consented (a GDPR
     * analytics leak). We now define the queue stub immediately — so the Gorgias
     * clarity('event', …) handler above still queues safely — but only inject the
     * tag once ANALYTICS consent is granted. Clarity is theme-loaded, so the
     * installed consent app cannot gate it — this is the one tracker the theme
     * still holds back itself. It reads the window.__consentLast breadcrumb
     * published by snippets/consent-mode.liquid and re-checks on that snippet's
     * 'altenew:consentapplied' event. If the breadcrumb never arrives, Clarity
     * stays unloaded (fail-closed).
     * ========================================================================= */
    (function (c, l, a, r, i) {
        c[a] = c[a] || function () {
            (c[a].q = c[a].q || []).push(arguments);
        };
        let injected = false;
        function loadClarity() {
            if (injected) return;
            if (!(c.__consentLast && c.__consentLast.analytics)) return;
            injected = true;
            const t = l.createElement(r);
            t.async = 1;
            t.src = 'https://www.clarity.ms/tag/' + i;
            const y = l.getElementsByTagName(r)[0];
            y.parentNode.insertBefore(t, y);
        }
        // Returning visitors / no-opt-in regions may already be granted by now;
        // otherwise wait for the banner choice to reconcile.
        loadClarity();
        l.addEventListener('altenew:consentapplied', loadClarity);
    })(window, document, 'clarity', 'script', '5g7h8ebllt');

    /* =========================================================================
     * Google Ads (AW-880895339) is GTM-owned — see the remarketing dedup note
     * in theme.liquid. Do not re-add a gtag config here: the duplicate config
     * hit is answered with image/gif and logs "Refused to execute script"
     * console errors on every page.
     * ========================================================================= */

  /* =========================================================================
   * Bottom spacing adjustments based on sticky bar & Rebuy PBar
   * ========================================================================= */
  ready(function () {
    const observedElements = new Set();

    const resizeObserver = new ResizeObserver(() => {
      adjustWidgets();
    });

    function adjustWidgets() {
      // The bar along the bottom is ours now (snippets/preview-bar.liquid);
      // Shopify's own #PBarNextFrameWrapper is suppressed but still in the DOM,
      // so it measures 0 and everything here would sit under our bar instead of
      // above it. Prefer whichever bar is actually on screen — shoppers have
      // neither, so they keep the original behaviour.
      const pbar = document.querySelector('.apb') || document.getElementById('PBarNextFrameWrapper');
      const stickyBag = document.getElementById('sticky-add-to-bag');
      const reamazeWidget = document.getElementById('reamaze-widget');
      const shopifyPcBanner = document.getElementById('shopify-pc__banner');
      const privacyDebugger = document.getElementById('privacy-debugger-root');

      let pbarHeight = 0;
      let stickyHeight = 0;

      if (pbar && pbar.offsetHeight) {
        pbarHeight = pbar.offsetHeight;
      }
      if (stickyBag && stickyBag.classList.contains('is-visible') && stickyBag.offsetHeight) {
        stickyHeight = stickyBag.offsetHeight;
      }

      // Sticky add-to-bag adjustment (pbar height)
      if (stickyBag) {
        stickyBag.style.setProperty('bottom', pbarHeight + 'px', 'important');
      }

      // Reamaze widget adjustment (sticky bag + pbar)
      if (reamazeWidget) {
        const reamazeDistance = 15;
        const reamazeBottom = stickyHeight + pbarHeight + reamazeDistance;
        reamazeWidget.style.setProperty('bottom', reamazeBottom + 'px', 'important');
        reamazeWidget.style.setProperty('left', reamazeDistance + 'px', 'important');
      }

      // Shopify privacy banner adjustment (pbar height)
      if (shopifyPcBanner) {
        shopifyPcBanner.style.setProperty('bottom', pbarHeight + 'px', 'important');
      }

      // Privacy debugger pill adjustment (sticky bag + pbar)
      if (privacyDebugger) {
        const debuggerDistance = 20;
        const debuggerBottom = stickyHeight + pbarHeight + debuggerDistance;
        privacyDebugger.style.setProperty('bottom', debuggerBottom + 'px', 'important');
      }
    }

    // adjustWidgets() reads offsetHeight right after writing styles, a forced
    // layout. It used to run on every mutation batch anywhere on the page and
    // on every scroll event; now at most once a frame (10/23/2026).
    let widgetsQueued = false;
    let mutationsPending = false;
    let stickyClassChanged = false;
    const scheduleWidgets = (fromMutation) => {
      if (fromMutation) mutationsPending = true;
      if (widgetsQueued) return;
      widgetsQueued = true;
      requestAnimationFrame(() => {
        widgetsQueued = false;
        if (mutationsPending) {
          mutationsPending = false;
          onMutations();
        } else {
          adjustWidgets();
        }
      });
    };

    const mutationObserver = new MutationObserver((mutations) => {
      if (!stickyClassChanged) {
        stickyClassChanged = mutations.some((m) =>
          m.type === 'attributes' && m.attributeName === 'class' && m.target.id === 'sticky-add-to-bag');
      }
      scheduleWidgets(true);
    });

    function onMutations() {
      // Same bar as adjustWidgets() measures — observing the suppressed one
      // would never fire, so hiding or showing ours would leave the widgets put.
      const pbar = document.querySelector('.apb') || document.getElementById('PBarNextFrameWrapper');
      const stickyBag = document.getElementById('sticky-add-to-bag');
      let changed = false;

      if (pbar && !observedElements.has(pbar)) {
        resizeObserver.observe(pbar);
        observedElements.add(pbar);
        changed = true;
      }
      if (stickyBag && !observedElements.has(stickyBag)) {
        resizeObserver.observe(stickyBag);
        observedElements.add(stickyBag);
        changed = true;
      }

      const classChanged = stickyClassChanged;
      stickyClassChanged = false;

      if (changed || classChanged || document.getElementById('reamaze-widget') || document.getElementById('shopify-pc__banner') || document.getElementById('sticky-add-to-bag') || document.getElementById('privacy-debugger-root')) {
        adjustWidgets();
      }
    }

    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    window.addEventListener('scroll', () => scheduleWidgets(false), { passive: true });
    adjustWidgets();
  });

  /* =========================================================================
   * Custom Element: badge-counter
   * ========================================================================= */
  class BadgeCounter extends HTMLElement {
      constructor() {
          super();
      }

      connectedCallback() {
          this.updateState();
          this.observer = new MutationObserver(() => this.updateState());
          this.observer.observe(this, {
              childList: true,
              characterData: true,
              subtree: true
          });
      }

      disconnectedCallback() {
          if (this.observer) {
              this.observer.disconnect();
          }
      }

      updateState() {
          const val = this.textContent.trim();
          if (val === '') {
              this.classList.add('badge-counter--empty');
              this.classList.remove('badge-counter--multi-digit');
          } else {
              this.classList.remove('badge-counter--empty');
              if (val.length > 2) {
                  this.classList.add('badge-counter--multi-digit');
              } else {
                  this.classList.remove('badge-counter--multi-digit');
              }
          }
      }
  }

  if (!customElements.get('badge-counter')) {
      customElements.define('badge-counter', BadgeCounter);
  }
})();



/* Unified cart reads: delegate the theme's cart getter to ShopifyCartManager
   (defined inline in theme.liquid) so theme code and apps share one coalesced,
   micro-cached request instead of firing independent /cart.js fetches. */
if (window.theme && window.theme.cart && window.ShopifyCartManager) {
  window.theme.cart.getCart = function () {
    return window.ShopifyCartManager.get();
  };
}

/* The OUT OF STOCK badge above the product name (snippets/product-oos-badge.liquid)
   follows the selected option. theme.js fires variant:change on every option
   change; a badge only reacts to its own product's options, so a quick view's
   choice never flips the page's badge. An option combination that doesn't exist
   sends no variant and leaves the badge as it was (ClickUp 14zcqntjbqb). */
document.addEventListener('variant:change', function (event) {
  const variant = event.detail && event.detail.variant;
  if (!variant) return;
  const id = ',' + variant.id + ',';
  document.querySelectorAll('.oos-title-badge').forEach(function (badge) {
    if (badge.dataset.variantIds.indexOf(id) === -1) return;
    badge.hidden = badge.dataset.soldOutIds.indexOf(id) === -1;
  });
});
